import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../scripts/AuthContext";
import { API_BASE_URL } from "@/lib/apiBase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AddToHomeScreenSettingsCard } from "./AddToHomeScreenOffer";

/**
 * Circa platform account settings: name, login email, password, social handle,
 * and permanent account deletion.
 */
export default function CircaAccountPanel({ user, onUserUpdated }) {
  const { accessToken, logout } = useAuth();
  const navigate = useNavigate();

  const [nameDraft, setNameDraft] = useState({
    first_name: "",
    last_name: "",
  });
  const [emailDraft, setEmailDraft] = useState("");
  const [profileMsg, setProfileMsg] = useState(null);
  const [profileErr, setProfileErr] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwMsg, setPwMsg] = useState(null);
  const [pwErr, setPwErr] = useState(null);
  const [pwSaving, setPwSaving] = useState(false);

  const [socialComplete, setSocialComplete] = useState(false);
  const [socialProfile, setSocialProfile] = useState(null);
  const [handleDraft, setHandleDraft] = useState("");
  const [handleMsg, setHandleMsg] = useState(null);
  const [handleErr, setHandleErr] = useState(null);
  const [handleSaving, setHandleSaving] = useState(false);
  const [socialLoading, setSocialLoading] = useState(true);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteErr, setDeleteErr] = useState(null);
  const [deleteSaving, setDeleteSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setNameDraft({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
    });
    setEmailDraft(user.email || "");
  }, [user]);

  const loadSocial = useCallback(async () => {
    if (!accessToken) return;
    setSocialLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/social/me/profile`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setSocialComplete(!!res.data?.complete);
      setSocialProfile(res.data?.profile || null);
      setHandleDraft(res.data?.profile?.username || "");
    } catch {
      setSocialComplete(false);
      setSocialProfile(null);
    } finally {
      setSocialLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadSocial();
  }, [loadSocial]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileMsg(null);
    setProfileErr(null);
    if (!nameDraft.first_name.trim() || !nameDraft.last_name.trim()) {
      setProfileErr("First and last name are required.");
      return;
    }
    if (!emailDraft.trim()) {
      setProfileErr("Platform email is required.");
      return;
    }
    setProfileSaving(true);
    try {
      const res = await axios.patch(
        `${API_BASE_URL}/user/me`,
        {
          first_name: nameDraft.first_name.trim(),
          last_name: nameDraft.last_name.trim(),
          email: emailDraft.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      onUserUpdated?.(res.data);
      setProfileMsg("Account details updated.");
    } catch (err) {
      setProfileErr(
        err?.response?.data?.message ||
          err?.message ||
          "Could not update account."
      );
    } finally {
      setProfileSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    setPwErr(null);
    if (newPassword.length < 6) {
      setPwErr("New password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwErr("New password and confirmation do not match.");
      return;
    }
    setPwSaving(true);
    try {
      await axios.post(
        `${API_BASE_URL}/user/me/password`,
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPwMsg("Password updated.");
    } catch (err) {
      setPwErr(
        err?.response?.data?.message ||
          err?.message ||
          "Could not change password."
      );
    } finally {
      setPwSaving(false);
    }
  };

  const saveHandle = async (e) => {
    e.preventDefault();
    setHandleMsg(null);
    setHandleErr(null);
    const next = handleDraft.trim().replace(/^@/, "");
    if (!next) {
      setHandleErr("Handle is required.");
      return;
    }
    if (
      socialProfile?.username &&
      next.toLowerCase() === String(socialProfile.username).toLowerCase()
    ) {
      setHandleMsg("Handle unchanged.");
      return;
    }
    setHandleSaving(true);
    try {
      const res = await axios.put(
        `${API_BASE_URL}/social/me/profile`,
        { username: next },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      setSocialComplete(!!res.data?.complete);
      setSocialProfile(res.data?.profile || null);
      setHandleDraft(res.data?.profile?.username || next);
      setHandleMsg("Handle updated.");
    } catch (err) {
      setHandleErr(
        err?.response?.data?.message ||
          err?.message ||
          "Could not update handle."
      );
    } finally {
      setHandleSaving(false);
    }
  };

  const deleteAccount = async () => {
    setDeleteErr(null);
    if (deleteConfirm !== "DELETE") {
      setDeleteErr('Type DELETE to confirm.');
      return;
    }
    if (!deletePassword) {
      setDeleteErr("Enter your password.");
      return;
    }
    setDeleteSaving(true);
    try {
      await axios.delete(`${API_BASE_URL}/user/me`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        data: { password: deletePassword, confirm: "DELETE" },
      });
      setDeleteOpen(false);
      await logout?.();
      navigate("/login", { replace: true });
    } catch (err) {
      setDeleteErr(
        err?.response?.data?.message ||
          err?.message ||
          "Could not delete account."
      );
    } finally {
      setDeleteSaving(false);
    }
  };

  const handleCanChange = socialProfile?.username_can_change !== false;
  const nextChangeAt = socialProfile?.username_next_change_at;
  const hasHandle = Boolean(socialProfile?.username);

  return (
    <div className="space-y-4">
      <AddToHomeScreenSettingsCard />
      <Card>
        <CardHeader>
          <CardTitle>Circa account</CardTitle>
          <CardDescription>
            Your platform name and login email. This is separate from each
            workspace&apos;s organization contact email.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {profileMsg ? (
            <Alert className="mb-4 border-green-700 bg-green-900/20 text-green-200">
              <AlertDescription>{profileMsg}</AlertDescription>
            </Alert>
          ) : null}
          {profileErr ? (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{profileErr}</AlertDescription>
            </Alert>
          ) : null}
          <form onSubmit={saveProfile} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="circa-first-name">First name</Label>
                <Input
                  id="circa-first-name"
                  value={nameDraft.first_name}
                  onChange={(e) =>
                    setNameDraft((p) => ({ ...p, first_name: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="circa-last-name">Last name</Label>
                <Input
                  id="circa-last-name"
                  value={nameDraft.last_name}
                  onChange={(e) =>
                    setNameDraft((p) => ({ ...p, last_name: e.target.value }))
                  }
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="circa-email">Platform email</Label>
              <Input
                id="circa-email"
                type="email"
                value={emailDraft}
                onChange={(e) => setEmailDraft(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                Used to sign in to Circa. Not the same as org contact email on
                the Workspace tab.
              </p>
            </div>
            <Button type="submit" disabled={profileSaving}>
              {profileSaving ? "Saving…" : "Save account details"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Password</CardTitle>
          <CardDescription>
            Change your Circa login password. You must enter your current
            password.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pwMsg ? (
            <Alert className="mb-4 border-green-700 bg-green-900/20 text-green-200">
              <AlertDescription>{pwMsg}</AlertDescription>
            </Alert>
          ) : null}
          {pwErr ? (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{pwErr}</AlertDescription>
            </Alert>
          ) : null}
          <form onSubmit={changePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="circa-current-password">Current password</Label>
              <Input
                id="circa-current-password"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="circa-new-password">New password</Label>
              <Input
                id="circa-new-password"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="circa-confirm-password">Confirm new password</Label>
              <Input
                id="circa-confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <Button type="submit" disabled={pwSaving}>
              {pwSaving ? "Updating…" : "Change password"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Circa Social handle</CardTitle>
          <CardDescription>
            Your public @{/* */}handle on Circa Social. You can rename it at most
            once every 6 months.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {socialLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : !socialComplete && !hasHandle ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                You haven&apos;t set up Circa Social yet.
              </p>
              <Button asChild variant="outline">
                <Link to="/social/setup">Set up Circa Social</Link>
              </Button>
            </div>
          ) : (
            <>
              {handleMsg ? (
                <Alert className="mb-4 border-green-700 bg-green-900/20 text-green-200">
                  <AlertDescription>{handleMsg}</AlertDescription>
                </Alert>
              ) : null}
              {handleErr ? (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{handleErr}</AlertDescription>
                </Alert>
              ) : null}
              <form onSubmit={saveHandle} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="circa-handle">Handle</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">@</span>
                    <Input
                      id="circa-handle"
                      value={handleDraft}
                      onChange={(e) => setHandleDraft(e.target.value)}
                      disabled={!handleCanChange || handleSaving}
                      className="font-mono"
                    />
                  </div>
                  {!handleCanChange && nextChangeAt ? (
                    <p className="text-xs text-muted-foreground">
                      Next change available{" "}
                      {new Date(nextChangeAt).toLocaleString(undefined, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                      .
                    </p>
                  ) : hasHandle ? (
                    <p className="text-xs text-muted-foreground">
                      Renaming starts a 6-month cooldown. Display name and bio
                      stay editable on your Social profile.
                    </p>
                  ) : null}
                </div>
                <Button
                  type="submit"
                  disabled={!handleCanChange || handleSaving}
                >
                  {handleSaving ? "Saving…" : "Update handle"}
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="text-destructive">Delete account</CardTitle>
          <CardDescription>
            Permanently delete your Circa platform account, Social profile, and
            leave all workspaces. You cannot delete your account while you still
            own an organization — transfer ownership first.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              setDeleteErr(null);
              setDeletePassword("");
              setDeleteConfirm("");
              setDeleteOpen(true);
            }}
          >
            Delete my Circa account
          </Button>
        </CardContent>
      </Card>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Circa account?</DialogTitle>
            <DialogDescription>
              This cannot be undone. Type DELETE and enter your password to
              confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {deleteErr ? (
              <Alert variant="destructive">
                <AlertDescription>{deleteErr}</AlertDescription>
              </Alert>
            ) : null}
            <div className="space-y-2">
              <Label htmlFor="delete-confirm">Type DELETE</Label>
              <Input
                id="delete-confirm"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="delete-password">Password</Label>
              <Input
                id="delete-password"
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              disabled={deleteSaving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={deleteAccount}
              disabled={deleteSaving}
            >
              {deleteSaving ? "Deleting…" : "Delete account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
